"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { Loader2, UserCheck, ScrollText, Trophy, Clock, Zap } from "@/lib/icons"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"

interface FeedItem {
  id: string
  type: "script" | "bounty"
  title: string
  thumbnail: string | null
  preview: string | null
  contentType: string | null
  durationMs: number | null
  intensity: number | null
  priceCents: number | null
  rewardCents?: number
  status?: string
  creatorId: string
  creatorUsername: string
  creatorAvatar: string | null
  publishedAt: string
}

function fmtDuration(ms: number): string {
  const s = Math.floor(ms / 1000)
  const m = Math.floor(s / 60)
  return `${m}:${(s % 60).toString().padStart(2, "0")}`
}

function fmtCents(cents: number): string {
  return cents === 0 ? "Free" : `$${(cents / 100).toFixed(2)}`
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

function FeedCard({ item, onClick }: { item: FeedItem; onClick: () => void }) {
  const t = useTranslations("followingFeed")
  const isScript = item.type === "script"

  return (
    // div + role="button" instead of a real <button> -- the creator name
    // below is now a real nested <Link> (for the goonpals creator profile
    // page), and a <button> can't validly contain another interactive
    // element like an <a>.
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick() } }}
      className="group flex flex-col rounded-lg overflow-hidden border border-border/40 bg-card hover:border-border/80 hover:shadow-md transition-all text-left w-full cursor-pointer"
    >
      {/* Thumbnail */}
      <div className="relative w-full aspect-video bg-muted/40 overflow-hidden">
        {item.thumbnail ? (
          <img
            src={item.thumbnail}
            alt={item.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {isScript
              ? <ScrollText className="h-8 w-8 text-muted-foreground/30" />
              : <Trophy className="h-8 w-8 text-muted-foreground/30" />
            }
          </div>
        )}

        {/* Type badge */}
        <div className="absolute top-1.5 left-1.5">
          <Badge
            variant="secondary"
            className={cn(
              "text-[10px] px-1.5 py-0 font-semibold",
              isScript ? "bg-violet-600/80 text-white" : "bg-amber-500/80 text-white"
            )}
          >
            {isScript ? t("script") : t("bounty")}
          </Badge>
        </div>

        {/* Duration for scripts */}
        {isScript && item.durationMs && (
          <div className="absolute bottom-1.5 right-1.5 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded font-mono">
            {fmtDuration(item.durationMs)}
          </div>
        )}

        {/* Reward for bounties */}
        {!isScript && item.rewardCents !== undefined && (
          <div className="absolute bottom-1.5 right-1.5 bg-amber-500/90 text-white text-[10px] px-1.5 py-0.5 rounded font-bold">
            ${(item.rewardCents / 100).toFixed(0)}
          </div>
        )}
      </div>

      {/* Info row */}
      <div className="p-2.5 space-y-1.5 flex-1">
        <p className="text-xs font-medium leading-snug line-clamp-2">{item.title}</p>

        <div className="flex items-center gap-1.5">
          {item.creatorAvatar ? (
            <img src={item.creatorAvatar} alt="" className="h-4 w-4 rounded-full object-cover shrink-0" />
          ) : (
            <div className="h-4 w-4 rounded-full bg-muted shrink-0" />
          )}
          {item.creatorUsername && item.creatorUsername !== "Unknown" ? (
            <Link
              href={`/goonpals/creator/${encodeURIComponent(item.creatorUsername)}`}
              onClick={(e) => e.stopPropagation()}
              className="text-[10px] text-muted-foreground truncate hover:text-foreground hover:underline"
            >
              {item.creatorUsername}
            </Link>
          ) : (
            <span className="text-[10px] text-muted-foreground truncate">{item.creatorUsername}</span>
          )}
          <span className="text-[10px] text-muted-foreground/50 ml-auto shrink-0 flex items-center gap-0.5">
            <Clock className="h-2.5 w-2.5" />
            {timeAgo(item.publishedAt)}
          </span>
        </div>

        {/* Script-specific: intensity + price */}
        {isScript && (
          <div className="flex items-center gap-1.5 pt-0.5">
            {item.intensity !== null && (
              <div className="flex items-center gap-0.5">
                <Zap className="h-3 w-3 text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground">{item.intensity}%</span>
              </div>
            )}
            <span className="ml-auto text-[10px] font-semibold text-foreground">
              {item.priceCents !== null ? fmtCents(item.priceCents) : ""}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

interface FollowingFeedProps {
  className?: string
}

export function FollowingFeed({ className }: FollowingFeedProps) {
  const t = useTranslations("followingFeed")
  const router = useRouter()
  const [items, setItems] = useState<FeedItem[]>([])
  const [followingCount, setFollowingCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    fetch("/api/following/feed")
      .then((r) => {
        if (!r.ok) throw new Error(r.status === 401 ? "auth" : "failed")
        return r.json()
      })
      .then((data) => {
        if (cancelled) return
        setItems(data.items ?? [])
        setFollowingCount(data.followingCount ?? 0)
      })
      .catch((e) => {
        if (!cancelled) setError(e.message === "auth" ? "auth" : "Could not load feed")
      })
      .finally(() => { if (!cancelled) setLoading(false) })

    return () => { cancelled = true }
  }, [])

  const handleItemClick = (item: FeedItem) => {
    if (item.type === "script") {
      router.push(`/marketplace?script=${item.id}`)
    } else {
      router.push(`/bounties/${item.id}`)
    }
  }

  if (loading) {
    return (
      <div className={cn("flex items-center justify-center py-20", className)}>
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error === "auth") {
    return (
      <div className={cn("flex flex-col items-center justify-center py-20 gap-3 text-center px-4", className)}>
        <UserCheck className="h-8 w-8 text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">{t("signInToSeeYourFollowingFeed")}</p>
      </div>
    )
  }

  if (followingCount === 0) {
    return (
      <div className={cn("flex flex-col items-center justify-center py-20 gap-3 text-center px-4", className)}>
        <UserCheck className="h-8 w-8 text-muted-foreground/40" />
        <p className="text-sm font-medium">{t("youReNotFollowingAnyoneYet")}</p>
        <p className="text-xs text-muted-foreground max-w-xs">
          {t("followCreatorsFromTheirProfilesToSeeTheir")}
        </p>
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className={cn("flex flex-col items-center justify-center py-20 gap-3 text-center px-4", className)}>
        <UserCheck className="h-8 w-8 text-muted-foreground/40" />
        <p className="text-sm font-medium">{t("nothingNewFromTheCreatorsYouFollow", { count: followingCount })}</p>
        <p className="text-xs text-muted-foreground">{t("checkBackLaterNewScriptsAndBountiesWill")}</p>
      </div>
    )
  }

  return (
    <div className={cn("px-3 py-3", className)}>
      <p className="text-[10px] text-muted-foreground mb-3">
        {t("itemsFromCreatorsYouFollow", { items: items.length, creators: followingCount })}
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5">
        {items.map((item) => (
          <FeedCard key={`${item.type}-${item.id}`} item={item} onClick={() => handleItemClick(item)} />
        ))}
      </div>
    </div>
  )
}
